/**
 * Network Helper Utilities
 * Common functions for working with network nodes and connections
 */

import RBush from 'rbush';
import type {
  Node,
  Connection,
  NodeType,
  Point,
  NodeProperties,
  ConnectionProperties,
  BusNode,
  GeneratorNode,
  LoadNode,
  StorageNode,
} from '@/types';
import { mergeWithDefaults } from '@/constants';

type BusIndexItem = {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
  node: Node;
};

const busIndexCache = new WeakMap<Node[], RBush<BusIndexItem>>();

function buildBusIndex(nodes: Node[]): RBush<BusIndexItem> {
  const index = new RBush<BusIndexItem>();
  const items: BusIndexItem[] = nodes
    .filter((n) => n.type === 'bus')
    .map((bus) => ({
      minX: bus.x,
      minY: bus.y,
      maxX: bus.x,
      maxY: bus.y,
      node: bus,
    }));

  if (items.length > 0) {
    index.load(items);
  }

  return index;
}

function getBusIndex(nodes: Node[]): RBush<BusIndexItem> {
  const cached = busIndexCache.get(nodes);
  if (cached) return cached;
  const index = buildBusIndex(nodes);
  busIndexCache.set(nodes, index);
  return index;
}

// ============================================================
// DISTANCE & PROXIMITY
// ============================================================

/**
 * Calculate the Euclidean distance between two points
 */
export function distance(p1: Point, p2: Point): number {
  return Math.hypot(p1.x - p2.x, p1.y - p2.y);
}

/**
 * Calculate the distance between two nodes
 */
export function nodeDistance(node1: Node, node2: Node): number {
  return distance({ x: node1.x, y: node1.y }, { x: node2.x, y: node2.y });
}

/**
 * Find the nearest bus to a given node
 * Used extensively for connecting generators, loads, and storage to buses
 */
export function findNearestBus(
  node: Node | Point,
  nodes: Node[],
  options: { excludeNodeId?: string; allowSelf?: boolean } = {}
): Node | null {
  const nodePoint: Point = 'type' in node ? { x: node.x, y: node.y } : node;
  const index = getBusIndex(nodes);

  if (index.all().length === 0) return null;

  const excludeId = options.excludeNodeId || ('type' in node && node.type === 'bus' && !options.allowSelf
    ? node.id
    : undefined);

  let radius = 10;
  let candidates: BusIndexItem[] = [];
  let filtered: BusIndexItem[] = [];

  while (radius < 10000) {
    candidates = index.search({
      minX: nodePoint.x - radius,
      minY: nodePoint.y - radius,
      maxX: nodePoint.x + radius,
      maxY: nodePoint.y + radius,
    });
    filtered = excludeId
      ? candidates.filter((item) => item.node.id !== excludeId)
      : candidates;
    if (filtered.length > 0) break;
    radius *= 2;
  }

  if (filtered.length === 0) return null;

  return filtered.reduce((nearest, item) => {
    const distToNearest = distance(nodePoint, { x: nearest.x, y: nearest.y });
    const distToBus = distance(nodePoint, { x: item.node.x, y: item.node.y });
    return distToBus < distToNearest ? item.node : nearest;
  }, filtered[0].node);
}

/**
 * Find all nodes within a given radius of a point
 */
export function findNodesInRadius(
  center: Point,
  nodes: Node[],
  radius: number
): Node[] {
  return nodes.filter((node) => distance(center, { x: node.x, y: node.y }) <= radius);
}

/**
 * Find the nearest node of any type to a given point
 */
export function findNearestNode(point: Point, nodes: Node[]): Node | null {
  if (nodes.length === 0) return null;

  return nodes.reduce((nearest, node) => {
    const distToNearest = distance(point, { x: nearest.x, y: nearest.y });
    const distToNode = distance(point, { x: node.x, y: node.y });
    return distToNode < distToNearest ? node : nearest;
  });
}

// ============================================================
// FILTERING & GROUPING
// ============================================================

/**
 * Filter nodes by type
 */
export function filterByType<T extends NodeType>(
  nodes: Node[],
  type: T
): Extract<Node, { type: T }>[] {
  return nodes.filter((node): node is Extract<Node, { type: T }> => node.type === type);
}

/**
 * Filter nodes by multiple types
 */
export function filterByTypes<T extends NodeType>(
  nodes: Node[],
  types: T[]
): Extract<Node, { type: T }>[] {
  const typeSet = new Set(types);
  return nodes.filter((node): node is Extract<Node, { type: T }> => typeSet.has(node.type as T));
}

/**
 * Group nodes by type
 */
export function groupByType(nodes: Node[]): Record<NodeType, Node[]> {
  const groups: Record<NodeType, Node[]> = {
    bus: [],
    generator: [],
    load: [],
    storage: [],
  };

  nodes.forEach((node) => {
    if (node.type in groups) {
      groups[node.type].push(node);
    }
  });

  return groups;
}

/**
 * Get a node by ID
 */
export function getNodeById(nodes: Node[], id: string): Node | undefined {
  return nodes.find((node) => node.id === id);
}

/**
 * Get a connection by ID
 */
export function getConnectionById(
  connections: Connection[],
  id: string
): Connection | undefined {
  return connections.find((conn) => conn.id === id);
}

/**
 * Get all connections for a specific node
 */
export function getNodeConnections(
  connections: Connection[],
  nodeId: string
): Connection[] {
  return connections.filter((conn) => conn.from === nodeId || conn.to === nodeId);
}

/**
 * Get all nodes connected to a specific node
 */
export function getConnectedNodes(
  nodes: Node[],
  connections: Connection[],
  nodeId: string
): Node[] {
  const connectedIds = new Set<string>();

  connections.forEach((conn) => {
    if (conn.from === nodeId) connectedIds.add(conn.to);
    if (conn.to === nodeId) connectedIds.add(conn.from);
  });

  return nodes.filter((node) => connectedIds.has(node.id));
}

// ============================================================
// FACTORY FUNCTIONS
// ============================================================

/**
 * Generate a unique ID for a node or connection
 */
export function generateId(prefix: string): string {
  return `${prefix}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Create a new node with default properties
 */
export function createNode(
  type: 'bus',
  position: Point,
  options?: {
    name?: string;
    properties?: Partial<NodeProperties>;
    id?: string;
  }
): BusNode;
export function createNode(
  type: 'generator',
  position: Point,
  options?: {
    name?: string;
    properties?: Partial<NodeProperties>;
    id?: string;
  }
): GeneratorNode;
export function createNode(
  type: 'load',
  position: Point,
  options?: {
    name?: string;
    properties?: Partial<NodeProperties>;
    id?: string;
  }
): LoadNode;
export function createNode(
  type: 'storage',
  position: Point,
  options?: {
    name?: string;
    properties?: Partial<NodeProperties>;
    id?: string;
  }
): StorageNode;
export function createNode(
  type: NodeType,
  position: Point,
  options?: {
    name?: string;
    properties?: Partial<NodeProperties>;
    id?: string;
  }
): Node;
export function createNode(
  type: NodeType,
  position: Point,
  options: {
    name?: string;
    properties?: Partial<NodeProperties>;
    id?: string;
  } = {}
): Node {
  const { name, properties = {}, id } = options;

  return {
    id: id || generateId(type),
    type,
    x: Math.round(position.x),
    y: Math.round(position.y),
    name: name || `${type.charAt(0).toUpperCase() + type.slice(1)}`,
    properties: mergeWithDefaults(type, properties),
  } as Node;
}

/**
 * Create a node with auto-generated name based on count
 */
export function createNodeWithAutoName(
  type: NodeType,
  position: Point,
  existingNodes: Node[],
  options: {
    label?: string;
    properties?: Partial<NodeProperties>;
  } = {}
): Node {
  const { label, properties } = options;
  const count = existingNodes.filter((n) => n.type === type).length + 1;
  const typeName = label || type.charAt(0).toUpperCase() + type.slice(1);

  return createNode(type, position, {
    name: `${typeName} ${count}`,
    properties,
  });
}

/**
 * Determine connection type based on connected nodes
 */
export function getConnectionType(
  fromNode: Node | undefined,
  toNode: Node | undefined
): 'line' | 'attachment' {
  if (fromNode?.type === 'bus' && toNode?.type === 'bus') {
    return 'line';
  }
  return 'attachment';
}

/**
 * Get default properties for a connection type
 */
export function getDefaultConnectionProperties(
  type: 'line' | 'attachment'
): ConnectionProperties {
  if (type === 'line') {
    return {
      r: 0.01,
      x: 0.1,
      s_nom: 100,
    };
  }
  return {};
}

/**
 * Create a new connection
 */
export function createConnection(
  fromId: string,
  toId: string,
  nodes: Node[],
  options: {
    id?: string;
    type?: 'line' | 'attachment';
    properties?: Partial<ConnectionProperties>;
  } = {}
): Connection {
  const fromNode = getNodeById(nodes, fromId);
  const toNode = getNodeById(nodes, toId);

  const connectionType = options.type || getConnectionType(fromNode, toNode);

  return {
    id: options.id || generateId('conn'),
    from: fromId,
    to: toId,
    type: connectionType,
    properties: {
      ...getDefaultConnectionProperties(connectionType),
      ...options.properties,
    } as ConnectionProperties,
  };
}

// ============================================================
// VALIDATION
// ============================================================

/**
 * Check if a connection already exists between two nodes
 */
export function connectionExists(
  connections: Connection[],
  fromId: string,
  toId: string
): boolean {
  return connections.some(
    (conn) =>
      (conn.from === fromId && conn.to === toId) ||
      (conn.from === toId && conn.to === fromId)
  );
}

/**
 * Check if a node ID is unique
 */
export function isUniqueNodeId(nodes: Node[], id: string): boolean {
  return !nodes.some((node) => node.id === id);
}

/**
 * Check if a node name is unique
 */
export function isUniqueNodeName(nodes: Node[], name: string): boolean {
  return !nodes.some((node) => node.name === name);
}

/**
 * Validate network for power flow analysis
 */
export function validateNetworkForPowerFlow(
  nodes: Node[],
  connections: Connection[]
): { valid: boolean; errors: string[] } {
  const errors: string[] = [];

  const buses = filterByType(nodes, 'bus');
  if (buses.length === 0) {
    errors.push('Network must have at least one bus');
  }

  const generators = filterByType(nodes, 'generator');
  const loads = filterByType(nodes, 'load');
  if (generators.length === 0 && loads.length === 0) {
    errors.push('Network must have at least one generator or load');
  }

  // Check for isolated buses (no connections)
  buses.forEach((bus) => {
    const busConnections = getNodeConnections(connections, bus.id);
    if (busConnections.length === 0) {
      errors.push(`Bus "${bus.name}" has no connections`);
    }
  });

  return {
    valid: errors.length === 0,
    errors,
  };
}

// ============================================================
// BOUNDS & GEOMETRY
// ============================================================

/**
 * Calculate bounding box of nodes
 */
export function calculateNodesBounds(
  nodes: Node[],
  padding: number = 0
): { minX: number; maxX: number; minY: number; maxY: number } | null {
  if (nodes.length === 0) return null;

  const xs = nodes.map((n) => n.x);
  const ys = nodes.map((n) => n.y);

  return {
    minX: Math.min(...xs) - padding,
    maxX: Math.max(...xs) + padding,
    minY: Math.min(...ys) - padding,
    maxY: Math.max(...ys) + padding,
  };
}

/**
 * Calculate center point of nodes
 */
export function calculateNodesCenter(nodes: Node[]): Point | null {
  if (nodes.length === 0) return null;

  const sumX = nodes.reduce((sum, n) => sum + n.x, 0);
  const sumY = nodes.reduce((sum, n) => sum + n.y, 0);

  return {
    x: sumX / nodes.length,
    y: sumY / nodes.length,
  };
}

/**
 * Move nodes by offset
 */
export function moveNodes(nodes: Node[], offset: Point): Node[] {
  return nodes.map((node) => ({
    ...node,
    x: node.x + offset.x,
    y: node.y + offset.y,
  }));
}

/**
 * Clone nodes with new IDs
 */
export function cloneNodes(
  nodes: Node[],
  offset: Point = { x: 50, y: 50 }
): Node[] {
  const idMap = new Map<string, string>();

  return nodes.map((node) => {
    const newId = generateId(node.type);
    idMap.set(node.id, newId);

    const base = {
      id: newId,
      x: node.x + offset.x,
      y: node.y + offset.y,
      name: `${node.name} (copy)`,
    };

    switch (node.type) {
      case 'bus':
        return { ...node, ...base, properties: { ...node.properties } };
      case 'generator':
        return { ...node, ...base, properties: { ...node.properties } };
      case 'load':
        return { ...node, ...base, properties: { ...node.properties } };
      case 'storage':
        return { ...node, ...base, properties: { ...node.properties } };
      default: {
        const _exhaustive: never = node;
        return _exhaustive;
      }
    }
  });
}
